import { DateTimeFormatter, ZonedDateTime } from '@js-joda/core';
import { Locale } from '@js-joda/locale_en-us';
import clsx from 'clsx';
import React from 'react';
import { useForm } from 'react-hook-form';

import { FormControlTextInput } from '#/shared/components/FormControl';

import { TypedSignatureOnChange } from './types/SignatureInput';
import { SignatureInputTypeCanvas } from './SignatureInputTypeCanvas';

type FormValues = {
  nameAsSignature: string;
};

type SignatureInputTypeProps = {
  nameAsSignature: string;
  signedAt?: ZonedDateTime;
  onChange: TypedSignatureOnChange;
};

export function SignatureInputType({ signedAt, nameAsSignature, onChange }: Readonly<SignatureInputTypeProps>) {
  const canvasRef = React.useRef<HTMLCanvasElement>(null);

  const { control, watch } = useForm<FormValues>({
    defaultValues: {
      nameAsSignature: nameAsSignature,
    },
    mode: 'onChange',
  });

  const newNameAsSignature = watch('nameAsSignature');

  React.useEffect(() => {
    if (nameAsSignature !== newNameAsSignature) {
      const newValue = canvasRef?.current ? canvasRef.current.toDataURL() : undefined;
      const newSignedAt = newNameAsSignature ? ZonedDateTime.now() : undefined;
      onChange(newValue, newSignedAt, newNameAsSignature);
    }
  }, [nameAsSignature, newNameAsSignature, onChange]);

  return (
    <div className="p-16 flex flex-col gap-16">
      <FormControlTextInput
        label="Full name"
        placeholder="Enter text"
        description="Enter your full name to generate an electronic signature."
        descriptionPosition="above"
        name={'nameAsSignature'}
        type="text"
        control={control}
        maxLength={65}
        rules={{
          maxLength: { value: 65, message: 'Maximum 65 characters' },
        }}
        errorLeadingIconName={'error'}
      />
      {!newNameAsSignature && (
        <div
          className={clsx(
            'min-h-[80px]',
            'flex flex-col justify-center align-middle',
            'text-on-surface-variant text-center',
          )}
        >
          <div>Signature will appear here</div>
        </div>
      )}

      {!!newNameAsSignature && (
        <div className={clsx('min-h-[80px]', 'flex flex-col gap-12 pt-4', 'text-on-surface-variant text-center')}>
          <SignatureInputTypeCanvas canvasRef={canvasRef} nameAsSignature={newNameAsSignature} />

          <div>
            Signed on {signedAt?.format(DateTimeFormatter.ofPattern('dd MMM yyyy HH:mm:ss').withLocale(Locale.ENGLISH))}
          </div>
        </div>
      )}
    </div>
  );
}
